import { useEffect, useState, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { debounce } from 'lodash';
import { RefreshCw, BarChart3, Coins, Wrench, Users } from 'lucide-react';
import styles from './ChatAnalytics.module.css';
import { webchatAnalyticsApi } from '../../lib/apiClient';
import { useWidgetsStore } from '../../features/widgets/store/widgetsStore';
import { useAuthStore } from '../../store/authStore';
import { UserRole } from '../../enums/userRole';
import { toast } from '../../lib/toast';
import Loader from '../../ui/Loader/Loader';
import { hasMinRole } from '../../lib/permissions';
import { useNavigate } from 'react-router';
import { formatNumber, formatTokens } from '../../lib/formatTokens';
import DateRangeFilter from '../../ui/dateRangeFilter/DateRangeFilter';
import { useToolsStore } from '../../store/appStore';

interface AnalyticsData {
  totalConversations: number;
  completedConversations: number;
  errorConversations: number;
  resolutionRate: number;
  escalationRate: number;
  avgMessagesPerConversation: number;
  conversationsOverTime: { date: string; total: number; completed: number; error: number }[];
  toolDistribution: { name: string; title: string; count: number; successRate: number }[];
  tokenUsage: {
    currentMonth: { inputTokens: number; outputTokens: number; totalTokens: number; voiceTokens: number };
    monthlyLimit: number;
    monthlyVoiceLimit: number;
    dailyBreakdown: { date: string; tokens: number; voiceTokens: number }[];
    dailyInputOutput: { date: string; inputTokens: number; outputTokens: number; cacheReadTokens?: number; cacheWriteTokens?: number; voiceTokens: number }[];
    byProvider: { provider: string; inputTokens: number; outputTokens: number; totalTokens: number }[];
    byWidget: { widgetId: string; totalTokens: number }[];
  };
  topUsers: { userId: string; email: string; name: string; totalTokens: number; conversationCount: number }[];
  activeUsersOverTime: { date: string; users: number }[];
  satisfaction: { positive: number; negative: number; total: number; score: number };
}

type TabId = 'overview' | 'tokens' | 'tools' | 'users';

const TABS: { id: TabId; label: string; icon: typeof BarChart3 }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'tokens', label: 'Token Usage', icon: Coins },
  { id: 'tools', label: 'Tool Performance', icon: Wrench },
  { id: 'users', label: 'Users', icon: Users },
];


const isUnknownValue = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase();
  return !normalized || normalized === 'unknown';
};

const getUserDisplayName = (user: AnalyticsData['topUsers'][number]) => {
  const name = user.name?.trim();
  const email = user.email?.trim();

  if (!isUnknownValue(name) && name) {
    return name;
  }

  if (!isUnknownValue(email) && email) {
    return email;
  }

  return `Unknown (${user.userId.slice(0, 8)})`;
};

const getUserSecondaryText = (user: AnalyticsData['topUsers'][number]) => {
  const email = user.email?.trim();

  if (!isUnknownValue(email) && email) {
    return email;
  }

  return `Unresolved user ID: ${user.userId.slice(0, 8)}`;
};

export default function ChatAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const { widgets, fetchWidgets } = useWidgetsStore();
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const [showCacheDetail, setShowCacheDetail] = useState(false);
  const displayWidgets = isSuperAdmin ? widgets : widgets.filter(w => w.isActive);
  const [widgetFilter, setWidgetFilter] = useState('');
  const knownWidgetIds = useMemo(() => new Set(widgets.map(w => w.id)), [widgets]);
  const tools = useToolsStore(state => state.tools);

  const deletedWidgetIds = useMemo(() => {
    if (!data) return [] as string[];
    return (data.tokenUsage.byWidget || [])
      .map(bw => bw.widgetId)
      .filter(id => id && !knownWidgetIds.has(id));
  }, [data, knownWidgetIds]);
  const navigate = useNavigate();

  useEffect(() => {
    if (widgets.length === 0) fetchWidgets({ silent: !isSuperAdmin });
  }, []);

  useEffect(() => {
    if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
      navigate('/');
    }
  }, [user])
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await webchatAnalyticsApi.get(
        dateFrom || undefined,
        dateTo || undefined,
        widgetFilter || undefined,
      );
      setData(result as AnalyticsData);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  const debouncedFetch = useMemo(() => debounce(() => fetchRef.current(), 300), []);
  useEffect(() => { debouncedFetch(); return () => debouncedFetch.cancel(); }, [dateFrom, dateTo, widgetFilter]);
  useEffect(() => () => debouncedFetch.cancel(), []);

  // Helper: widget name lookup — falls back to "[Deleted] <prefix>" for removed widgets
  const widgetName = (id: string) => widgets.find(w => w.id === id)?.name || `[Deleted] ${id.slice(0, 8)}`;

  // ===== OVERVIEW CHARTS =====

  const conversationsChartOption = useMemo(() => {
    if (!data) return {};
    return {
      tooltip: { trigger: 'axis' as const },
      legend: { data: ['Total', 'Completed', 'Error'], bottom: 0 },
      grid: { left: 40, right: 20, top: 10, bottom: 40 },
      xAxis: { type: 'category' as const, data: data.conversationsOverTime.map(d => d.date), axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value' as const, minInterval: 1 },
      series: [
        { name: 'Total', type: 'line', data: data.conversationsOverTime.map(d => d.total), smooth: true, areaStyle: { opacity: 0.08 }, itemStyle: { color: '#6366f1' } },
        { name: 'Completed', type: 'line', data: data.conversationsOverTime.map(d => d.completed), smooth: true, itemStyle: { color: '#059669' } },
        { name: 'Error', type: 'line', data: data.conversationsOverTime.map(d => d.error), smooth: true, itemStyle: { color: '#dc2626' } },
      ],
    };
  }, [data]);

  const tokenPct = useMemo(() => {
    if (!data || data.tokenUsage.monthlyLimit <= 0) return 0;
    return Math.min((data.tokenUsage.currentMonth.totalTokens / data.tokenUsage.monthlyLimit) * 100, 100);
  }, [data]);

  const voiceTokenPct = useMemo(() => {
    if (!data || data.tokenUsage.monthlyVoiceLimit <= 0) return 0;
    return Math.min((data.tokenUsage.currentMonth.voiceTokens / data.tokenUsage.monthlyVoiceLimit) * 100, 100);
  }, [data]);

  const tokenGaugeOption = useMemo(() => {
    if (!data) return {};
    return {
      series: [{
        type: 'gauge', startAngle: 220, endAngle: -40, radius: '90%',
        progress: { show: true, width: 14, itemStyle: { color: tokenPct > 85 ? '#dc2626' : tokenPct > 60 ? '#f59e0b' : '#059669' } },
        axisLine: { lineStyle: { width: 14, color: [[1, '#e5e7eb']] } },
        axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
        pointer: { show: false }, title: { show: false },
        detail: { fontSize: 16, fontWeight: 700, offsetCenter: [0, '10%'], formatter: () => `${formatTokens(data.tokenUsage.currentMonth.totalTokens)}`, color: '#1f2937' },
        data: [{ value: Math.round(tokenPct) }],
      }],
    };
  }, [data, tokenPct]);

  // ===== TOKENS & COST CHARTS =====

  const dailyInputOutputOption = useMemo(() => {
    if (!data) return {};
    const io = data.tokenUsage.dailyInputOutput || [];
    const voiceSeries = { name: 'Voice Tokens', type: 'line', data: io.map(d => d.voiceTokens), smooth: true, itemStyle: { color: '#0f766e' }, lineStyle: { width: 2, color: '#0f766e' } };

    if (isSuperAdmin && showCacheDetail) {
      return {
        tooltip: { trigger: 'axis' as const },
        legend: { data: ['Input Tokens', 'Cache Read', 'Cache Write', 'Output Tokens', 'Voice Tokens'], bottom: 0 },
        grid: { left: 60, right: 20, top: 20, bottom: 40 },
        xAxis: { type: 'category' as const, data: io.map(d => d.date), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value' as const, axisLabel: { formatter: (v: number) => formatTokens(v) } },
        series: [
          { name: 'Input Tokens', type: 'bar', stack: 'tokens', data: io.map(d => d.inputTokens), itemStyle: { color: '#6366f1' }, barMaxWidth: 20 },
          { name: 'Cache Read', type: 'bar', stack: 'tokens', data: io.map(d => d.cacheReadTokens ?? 0), itemStyle: { color: '#f59e0b' }, barMaxWidth: 20 },
          { name: 'Cache Write', type: 'bar', stack: 'tokens', data: io.map(d => d.cacheWriteTokens ?? 0), itemStyle: { color: '#f97316' }, barMaxWidth: 20 },
          { name: 'Output Tokens', type: 'bar', stack: 'tokens', data: io.map(d => d.outputTokens), itemStyle: { color: '#a78bfa', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 20 },
          voiceSeries,
        ],
      };
    }

    return {
      tooltip: { trigger: 'axis' as const },
      legend: { data: ['Input Tokens', 'Output Tokens', 'Voice Tokens'], bottom: 0 },
      grid: { left: 60, right: 20, top: 20, bottom: 40 },
      xAxis: { type: 'category' as const, data: io.map(d => d.date), axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value' as const, axisLabel: { formatter: (v: number) => formatTokens(v) } },
      series: [
        {
          name: 'Input Tokens', type: 'bar', stack: 'tokens', barMaxWidth: 20,
          data: io.map(d => d.inputTokens + (d.cacheReadTokens || 0) + (d.cacheWriteTokens || 0)),
          itemStyle: { color: '#6366f1' },
        },
        { name: 'Output Tokens', type: 'bar', stack: 'tokens', data: io.map(d => d.outputTokens), itemStyle: { color: '#a78bfa', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 20 },
        voiceSeries,
      ],
    };
  }, [data, isSuperAdmin, showCacheDetail]);

  const providerPieOption = useMemo(() => {
    if (!data) return {};
    const bp = data.tokenUsage.byProvider || [];
    return {
      tooltip: { trigger: 'item' as const, formatter: (p: any) => `${p.name}: ${formatTokens(p.value)} tokens (${p.percent}%)` },
      series: [{
        type: 'pie', radius: ['45%', '70%'], center: ['50%', '50%'],
        label: { formatter: '{b}\n{d}%', fontSize: 11 },
        data: bp.map(p => ({ name: p.provider, value: p.totalTokens })),
        itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
      }],
    };
  }, [data]);

  const widgetTokensOption = useMemo(() => {
    if (!data) return {};
    const bw = (data.tokenUsage.byWidget || []).slice(0, 10);
    return {
      tooltip: { trigger: 'axis' as const },
      grid: { left: 140, right: 30, top: 10, bottom: 20 },
      xAxis: { type: 'value' as const, axisLabel: { formatter: (v: number) => formatTokens(v) } },
      yAxis: {
        type: 'category' as const,
        data: bw.map(w => widgetName(w.widgetId)),
        inverse: true,
        axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' as const },
      },
      series: [{
        type: 'bar',
        data: bw.map(w => w.totalTokens),
        itemStyle: { color: '#8b5cf6', borderRadius: [0, 4, 4, 0] },
        barMaxWidth: 24,
      }],
    };
  }, [data, widgets]);

  // ===== TOOL PERFORMANCE CHARTS =====

  const toolDistTop10 = useMemo(() => data?.toolDistribution.slice(0, 10) ?? [], [data]);

  const toolUsageChartOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis' as const, formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params;
        const tool = toolDistTop10[p.dataIndex];
        return `<b>${tool?.name}</b><br/>Calls: ${tool?.count}<br/>Success: ${tool?.successRate}%`;
      }
    },
    grid: { left: 140, right: 30, top: 10, bottom: 20 },
    xAxis: { type: 'value' as const, minInterval: 1 },
    yAxis: {
      type: 'category' as const,
      data: toolDistTop10.map(t => t.name),
      inverse: true,
      axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' as const },
    },
    series: [{
      type: 'bar',
      data: toolDistTop10.map(t => t.count),
      itemStyle: { color: '#6366f1', borderRadius: [0, 4, 4, 0] },
      barMaxWidth: 24,
    }],
  }), [toolDistTop10]);

  const toolSuccessChartOption = useMemo(() => ({
    tooltip: { trigger: 'axis' as const },
    grid: { left: 140, right: 30, top: 10, bottom: 20 },
    xAxis: { type: 'value' as const, min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
    yAxis: {
      type: 'category' as const,
      data: toolDistTop10.map(t => t.name),
      inverse: true,
      axisLabel: { fontSize: 11, width: 120, overflow: 'truncate' as const },
    },
    series: [{
      type: 'bar',
      data: toolDistTop10.map(t => ({
        value: t.successRate,
        itemStyle: { color: t.successRate >= 90 ? '#059669' : t.successRate >= 70 ? '#f59e0b' : '#dc2626' },
      })),
      barMaxWidth: 24,
      label: { show: true, position: 'right' as const, formatter: '{c}%', fontSize: 11 },
    }],
  }), [toolDistTop10]);

  // ===== USERS CHARTS =====

  const activeUsersChartOption = useMemo(() => {
    if (!data) return {};
    const au = data.activeUsersOverTime || [];
    return {
      tooltip: { trigger: 'axis' as const },
      grid: { left: 40, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category' as const, data: au.map(d => d.date), axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value' as const, minInterval: 1 },
      series: [{
        type: 'line', data: au.map(d => d.users),
        smooth: true, areaStyle: { opacity: 0.1 }, itemStyle: { color: '#0284c7' },
      }],
    };
  }, [data]);

  const topUsersChartOption = useMemo(() => {
    if (!data) return {};
    const tu = (data.topUsers || []).slice(0, 10);
    return {
      tooltip: {
        trigger: 'axis' as const, formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const u = tu[p.dataIndex];
          return `<b>${u ? getUserDisplayName(u) : 'Unknown'}</b><br/>Tokens: ${formatTokens(u?.totalTokens || 0)}<br/>Conversations: ${u?.conversationCount || 0}`;
        }
      },
      grid: { left: 160, right: 30, top: 10, bottom: 20 },
      xAxis: { type: 'value' as const, axisLabel: { formatter: (v: number) => formatTokens(v) } },
      yAxis: {
        type: 'category' as const,
        data: tu.map(getUserDisplayName),
        inverse: true,
        axisLabel: { fontSize: 11, width: 140, overflow: 'truncate' as const },
      },
      series: [{
        type: 'bar',
        data: tu.map(u => u.totalTokens),
        itemStyle: { color: '#0284c7', borderRadius: [0, 4, 4, 0] },
        barMaxWidth: 24,
      }],
    };
  }, [data]);

  const satScore = data?.satisfaction.score ?? 0;

  // --- Early returns ---
  if (loading) return <div className={styles.loading}><Loader /></div>;
  if (!data) return <div className={styles.empty}>No analytics data available.</div>;

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Chat AI Analytics</h2>
        <div className={styles.headerActions}>
          <select value={widgetFilter} onChange={e => setWidgetFilter(e.target.value)} className={styles.filterSelect}>
            <option value="">All AI Agents</option>
            {displayWidgets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            {deletedWidgetIds.map(id => <option key={id} value={id}>[Deleted] {id.slice(0, 8)}</option>)}
          </select>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: f, dateTo: t }) => { setDateFrom(f); setDateTo(t); }}
          />
          <button className={styles.refreshBtn} onClick={fetchData}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsRow}>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`${styles.tabBtn} ${activeTab === t.id ? styles.tabBtnActive : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <>
          <div className={styles.kpiRow}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Conversations</div>
              <div className={styles.kpiValue}>{formatNumber(data.totalConversations)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Resolution Rate</div>
              <div className={styles.kpiValueGreen}>{data.resolutionRate.toFixed(1)}%</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Escalation Rate</div>
              <div className={styles.kpiValueRed}>{data.escalationRate.toFixed(1)}%</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Avg Messages</div>
              <div className={styles.kpiValue}>{data.avgMessagesPerConversation.toFixed(1)}</div>
            </div>
            <div className={styles.tokenGaugeCard}>
              <div className={styles.tokenGaugeLabel}>Token Usage</div>
              <ReactECharts option={tokenGaugeOption} style={{ width: '100%', height: 80 }} />
              <div className={styles.tokenGaugeMeta}>
                {formatTokens(data.tokenUsage.currentMonth.totalTokens)} / {formatTokens(data.tokenUsage.monthlyLimit)} limit
              </div>
            </div>
            <div className={styles.satisfactionKpi}>
              <div className={styles.kpiLabel}>Satisfaction</div>
              <div className={styles.kpiValueGreen}>{satScore.toFixed(0)}%</div>
              <div className={styles.satisfactionMiniBar}>
                <div className={styles.satisfactionMiniFill} style={{ width: `${satScore}%` }} />
              </div>
              <div className={styles.satisfactionMeta}>{data.satisfaction.positive}/{data.satisfaction.total} positive</div>
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCardFull}>
              <div className={styles.chartTitle}>Conversations Over Time</div>
              <ReactECharts option={conversationsChartOption} style={{ height: 260 }} />
            </div>
          </div>

          <div className={styles.chartsRow}>

            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Tool / Intent Distribution</div>
              {toolDistTop10.length > 0 ? (
                <ReactECharts option={toolUsageChartOption} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No tool usage data yet.</div>
              )}
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitleRow}>
                <div className={styles.chartTitle}>Daily Token Usage</div>
                {isSuperAdmin && (
                  <label className={styles.cacheToggle}>
                    <input type="checkbox" checked={showCacheDetail} onChange={e => setShowCacheDetail(e.target.checked)} />
                    Show cache breakdown
                  </label>
                )}
              </div>
              {(data.tokenUsage.dailyInputOutput || []).length > 0 ? (
                <ReactECharts option={dailyInputOutputOption} replaceMerge={['series']} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No token usage data yet.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== TOKEN USAGE TAB ===== */}
      {activeTab === 'tokens' && (
        <>
          {/* Monthly limit progress */}
          <div className={styles.limitCard}>
            <div className={styles.limitHeader}>
              <div>
                <div className={styles.limitTitle}>Monthly Token Budget</div>
                <div className={styles.limitNumbers}>
                  <span className={styles.limitUsed}>{formatTokens(data.tokenUsage.currentMonth.totalTokens)}</span>
                  <span className={styles.limitSep}> / </span>
                  <span className={styles.limitTotal}>{formatTokens(data.tokenUsage.monthlyLimit)}</span>
                </div>
              </div>
              <div className={styles.limitPct} style={{
                color: tokenPct > 85 ? '#dc2626' : tokenPct > 60 ? '#f59e0b' : '#059669',
              }}>
                {Math.round(tokenPct)}%
              </div>
            </div>
            <div className={styles.limitBar}>
              <div
                className={styles.limitBarFill}
                style={{
                  width: `${tokenPct}%`,
                  background: tokenPct > 85 ? '#dc2626' : tokenPct > 60 ? '#f59e0b' : '#059669',
                }}
              />
            </div>
            <div className={styles.limitMeta}>
              {data.tokenUsage.monthlyLimit > 0
                ? `${formatTokens(Math.max(0, data.tokenUsage.monthlyLimit - data.tokenUsage.currentMonth.totalTokens))} remaining`
                : 'No monthly limit configured'}
            </div>
          </div>

          <div className={styles.limitCard}>
            <div className={styles.limitHeader}>
              <div>
                <div className={styles.limitTitle}>Monthly Voice Budget</div>
                <div className={styles.limitNumbers}>
                  <span className={styles.limitUsed}>{formatTokens(data.tokenUsage.currentMonth.voiceTokens)}</span>
                  <span className={styles.limitSep}> / </span>
                  <span className={styles.limitTotal}>{formatTokens(data.tokenUsage.monthlyVoiceLimit)}</span>
                </div>
              </div>
              <div className={styles.limitPct} style={{
                color: voiceTokenPct > 85 ? '#dc2626' : voiceTokenPct > 60 ? '#f59e0b' : '#0f766e',
              }}>
                {Math.round(voiceTokenPct)}%
              </div>
            </div>
            <div className={styles.limitBar}>
              <div
                className={styles.limitBarFill}
                style={{
                  width: `${voiceTokenPct}%`,
                  background: voiceTokenPct > 85 ? '#dc2626' : voiceTokenPct > 60 ? '#f59e0b' : '#0f766e',
                }}
              />
            </div>
            <div className={styles.limitMeta}>
              {data.tokenUsage.monthlyVoiceLimit > 0
                ? `${formatTokens(Math.max(0, data.tokenUsage.monthlyVoiceLimit - data.tokenUsage.currentMonth.voiceTokens))} remaining`
                : 'No monthly voice limit configured'}
            </div>
          </div>

          <div className={styles.kpiRow5}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Tokens (Period)</div>
              <div className={styles.kpiValue}>{formatTokens(data.tokenUsage.currentMonth.totalTokens)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Input Tokens</div>
              <div className={styles.kpiValue}>{formatTokens(data.tokenUsage.currentMonth.inputTokens)}</div>
              <div className={styles.kpiSub}>{data.tokenUsage.currentMonth.totalTokens > 0 ? Math.round((data.tokenUsage.currentMonth.inputTokens / data.tokenUsage.currentMonth.totalTokens) * 100) : 0}% of total</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Output Tokens</div>
              <div className={styles.kpiValue}>{formatTokens(data.tokenUsage.currentMonth.outputTokens)}</div>
              <div className={styles.kpiSub}>{data.tokenUsage.currentMonth.totalTokens > 0 ? Math.round((data.tokenUsage.currentMonth.outputTokens / data.tokenUsage.currentMonth.totalTokens) * 100) : 0}% of total</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Voice Tokens</div>
              <div className={styles.kpiValue}>{formatTokens(data.tokenUsage.currentMonth.voiceTokens)}</div>
              <div className={styles.kpiSub}>{data.tokenUsage.monthlyVoiceLimit > 0 ? `${Math.round(voiceTokenPct)}% of voice budget used` : 'No voice limit configured'}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Monthly Limit</div>
              <div className={styles.kpiValue}>{formatTokens(data.tokenUsage.monthlyLimit)}</div>
              <div className={styles.kpiSub}>{data.tokenUsage.monthlyLimit > 0 ? `${Math.round(tokenPct)}% used` : 'Unlimited'}</div>
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCardFull}>
              <div className={styles.chartTitleRow}>
                <div className={styles.chartTitle}>Daily Token Usage (Input, Output, Voice)</div>
                {isSuperAdmin && (
                  <label className={styles.cacheToggle}>
                    <input type="checkbox" checked={showCacheDetail} onChange={e => setShowCacheDetail(e.target.checked)} />
                    Show cache breakdown
                  </label>
                )}
              </div>
              {(data.tokenUsage.dailyInputOutput || []).length > 0 ? (
                <ReactECharts option={dailyInputOutputOption} replaceMerge={['series']} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No token usage data yet.</div>
              )}
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Usage by Provider</div>
              {(data.tokenUsage.byProvider || []).length > 0 ? (
                <>
                  <ReactECharts option={providerPieOption} style={{ height: 260 }} />
                  <table className={styles.dataTable}>
                    <thead>
                      <tr><th>Provider</th><th>Input</th><th>Output</th><th>Total</th></tr>
                    </thead>
                    <tbody>
                      {data.tokenUsage.byProvider.map(p => (
                        <tr key={p.provider}>
                          <td>{p.provider}</td>
                          <td>{formatTokens(p.inputTokens)}</td>
                          <td>{formatTokens(p.outputTokens)}</td>
                          <td>{formatTokens(p.totalTokens)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className={styles.empty}>No provider data yet.</div>
              )}
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Usage by AI Agent</div>
              {(data.tokenUsage.byWidget || []).length > 0 ? (
                <ReactECharts option={widgetTokensOption} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No per-agent data yet.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== TOOL PERFORMANCE TAB ===== */}
      {activeTab === 'tools' && (
        <>
          <div className={styles.kpiRow4}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Tool Calls</div>
              <div className={styles.kpiValue}>{formatNumber(data.toolDistribution.reduce((s, t) => s + t.count, 0))}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Unique Tools Used</div>
              <div className={styles.kpiValue}>{data.toolDistribution.length}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Avg Success Rate</div>
              <div className={data.toolDistribution.length > 0 ? styles.kpiValueGreen : styles.kpiValue}>
                {data.toolDistribution.length > 0
                  ? (data.toolDistribution.reduce((s, t) => s + t.successRate, 0) / data.toolDistribution.length).toFixed(0)
                  : 0}%
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Lowest Success Tool</div>
              {(() => {
                const worst = data.toolDistribution.length > 0
                  ? [...data.toolDistribution].sort((a, b) => a.successRate - b.successRate)[0]
                  : null;
                if (!worst) return <div className={styles.kpiValue}>N/A</div>;
                if (worst.successRate >= 90) return <div className={styles.kpiValueGreen} style={{ fontSize: '0.9rem' }}>All healthy</div>;
                return (
                  <div className={worst.successRate >= 70 ? styles.kpiValue : styles.kpiValueRed} style={{ fontSize: '0.9rem' }}>
                    {worst.name} ({worst.successRate}%)
                  </div>
                );
              })()}
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Tool / Intent Distribution</div>
              {toolDistTop10.length > 0 ? (
                <ReactECharts option={toolUsageChartOption} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No tool usage data yet.</div>
              )}
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Tool Success Rate</div>
              {toolDistTop10.length > 0 ? (
                <ReactECharts option={toolSuccessChartOption} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No tool usage data yet.</div>
              )}
            </div>
          </div>

          {/* Full tool table */}
          {data.toolDistribution.length > 0 && (
            <div className={styles.chartsRow}>
              <div className={styles.chartCardFull}>
                <div className={styles.chartTitle}>All Tools</div>
                <table className={styles.dataTable}>
                  <thead>
                    <tr><th>Tool Name</th><th>Calls</th><th>Success Rate</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {data.toolDistribution.map(t => (
                      <tr key={t.name}>
                        <td>{tools.find(tool => tool.name === t.name)?.title || t.name}</td>
                        <td>{formatNumber(t.count)}</td>
                        <td>{t.successRate}%</td>
                        <td>
                          <span className={t.successRate >= 90 ? styles.badgeGreen : t.successRate >= 70 ? styles.badgeYellow : styles.badgeRed}>
                            {t.successRate >= 90 ? 'Healthy' : t.successRate >= 70 ? 'Warning' : 'Critical'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== USERS TAB ===== */}
      {activeTab === 'users' && (
        <>
          <div className={styles.kpiRow4}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Conversations</div>
              <div className={styles.kpiValue}>{formatNumber(data.totalConversations)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Unique Users (Period)</div>
              <div className={styles.kpiValue}>
                {(data.activeUsersOverTime || []).length > 0
                  ? new Set((data.topUsers || []).map(u => u.userId)).size
                  : 0}
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Avg Tokens / User</div>
              <div className={styles.kpiValue}>
                {(data.topUsers || []).length > 0
                  ? formatTokens(Math.round(data.topUsers.reduce((s, u) => s + u.totalTokens, 0) / data.topUsers.length))
                  : '0'}
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Top Consumer</div>
              <div className={styles.kpiValue} style={{ fontSize: '0.9rem' }}>
                {(data.topUsers || []).length > 0
                  ? getUserDisplayName(data.topUsers[0])
                  : 'N/A'}
              </div>
              {(data.topUsers || []).length > 0 && (
                <div className={styles.kpiSub}>{formatTokens(data.topUsers[0].totalTokens)} tokens</div>
              )}
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCardFull}>
              <div className={styles.chartTitle}>Active Users Over Time</div>
              {(data.activeUsersOverTime || []).length > 0 ? (
                <ReactECharts option={activeUsersChartOption} style={{ height: 260 }} />
              ) : (
                <div className={styles.empty}>No user activity data yet.</div>
              )}
            </div>
          </div>

          <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Top Users by Token Usage</div>
              {(data.topUsers || []).length > 0 ? (
                <ReactECharts option={topUsersChartOption} style={{ height: 300 }} />
              ) : (
                <div className={styles.empty}>No user data yet.</div>
              )}
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>User Details</div>
              {(data.topUsers || []).length > 0 ? (
                <table className={styles.dataTable}>
                  <thead>
                    <tr><th>User</th><th>Conversations</th><th>Tokens</th></tr>
                  </thead>
                  <tbody>
                    {data.topUsers.map(u => (
                      <tr key={u.userId}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{getUserDisplayName(u)}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{getUserSecondaryText(u)}</div>
                        </td>
                        <td>{u.conversationCount}</td>
                        <td>{formatTokens(u.totalTokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.empty}>No user data yet.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
